import { useEffect } from "react";

import { requestActivityPing } from "../webclients/pin.client";

export const HEARTBEAT_INTERVAL_MS = 60_000;

const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "scroll",
  "touchstart",
] as const;

/**
 * The server locks a session after 3 minutes without an API call, but a user
 * reading a page or typing a long form makes none (MED-1620). While `enabled`,
 * this pings POST /account/activity at most once a minute, only when the user
 * did something since the last ping and the tab is visible. A 423 answer locks
 * the screen through the Axios interceptor.
 */
export const useActivityHeartbeat = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return;
    let active = false;
    let lastPing = Date.now();

    const onActivity = () => {
      active = true;
    };

    const tick = () => {
      if (!active || document.visibilityState !== "visible") return;
      if (Date.now() - lastPing < HEARTBEAT_INTERVAL_MS) return;
      active = false;
      lastPing = Date.now();
      requestActivityPing().catch(() => {
        // Fire-and-forget: a 423 is handled by the interceptor.
      });
    };

    ACTIVITY_EVENTS.forEach(name =>
      document.addEventListener(name, onActivity, {
        capture: true,
        passive: true,
      })
    );
    const timer = setInterval(tick, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(timer);
      ACTIVITY_EVENTS.forEach(name =>
        document.removeEventListener(name, onActivity, { capture: true })
      );
    };
  }, [enabled]);
};
